import clsx from 'clsx';
import { SEND_CONDITION_OPTIONS } from '../../../../../../shared/constants/sendCondition';
import { Select } from '../../../../../../shared/ui/select/Select';
import { TextField } from '../../../../../../shared/ui/textfield';
import { IconButton } from '../../../../../../shared/ui/icon-button';
import { AddRowButton } from '../../../../../../shared/ui/add-row-button';
import { TrashMutedIcon } from '../../../../../../shared/ui/icons';
import { sanitizeDayInput } from '../lib/sendConditionHelpers';
import styles from '../BasicInformationCard.module.css';

export function SendConditionsField({
  sendConditions,
  onChangeSendConditionAt,
  onRemoveSendConditionAt,
  onAddSendCondition,
  onFieldBlur,
  canAddNext = true,
  rowErrors = [],
}) {
  // The remove column appears on every row (and is reserved in the header for
  // alignment) once there is more than one row.
  const showRemoveColumn = sendConditions.length > 1;

  return (
    <div className={clsx(styles.sendConditionsGroup)}>
      <div className={clsx(styles.fieldGroup)}>
        <div className={clsx(styles.sendConditionRow)}>
          <div className={clsx(styles.sendConditionFields)}>
            <span className={clsx(styles.label, styles.sendConditionMain)}>Send Conditions</span>
            <span className={clsx(styles.label, styles.sendConditionDays)}>Days</span>
          </div>
          {showRemoveColumn ? (
            <span className={clsx(styles.sendConditionRemove)} aria-hidden />
          ) : null}
        </div>

        <div className={clsx(styles.sendConditionsList)}>
          {sendConditions.map((row, idx) => {
            const rowError = rowErrors[idx] ?? {};
            const hasRowError = Boolean(rowError.sendCondition || rowError.day);
            return (
              <div key={idx} className={clsx(styles.sendConditionItem)}>
                <div className={clsx(styles.sendConditionRow)}>
                  <div className={clsx(styles.sendConditionFields)}>
                    <div className={clsx(styles.sendConditionMain)}>
                      <Select
                        inputId={`send-condition-${idx}`}
                        value={row.sendCondition}
                        options={SEND_CONDITION_OPTIONS}
                        onChange={(val) =>
                          onChangeSendConditionAt(idx, { sendCondition: String(val) })
                        }
                        ariaLabel={`Send Condition ${idx + 1}`}
                        variant="form"
                        menuWidth="field"
                        invalid={Boolean(rowError.sendCondition)}
                        maxMenuRows={5}
                      />
                    </div>
                    <div className={clsx(styles.sendConditionDays)}>
                      <TextField
                        value={row.day}
                        onChange={(val) =>
                          onChangeSendConditionAt(idx, { day: sanitizeDayInput(val, row.day) })
                        }
                        onBlur={onFieldBlur}
                        placeholder="Enter a value between 1 and 30 days."
                        ariaLabel={`Days ${idx + 1}`}
                        variant="form"
                        invalid={Boolean(rowError.day) || Boolean(rowError.dayInvalid)}
                      />
                    </div>
                  </div>
                  {showRemoveColumn ? (
                    <div className={clsx(styles.sendConditionRemove)}>
                      <IconButton
                        ariaLabel={`Remove Send Condition ${idx + 1}`}
                        onClick={() => onRemoveSendConditionAt(idx)}
                        size="md"
                      >
                        <TrashMutedIcon className={clsx(styles.trashIcon)} />
                      </IconButton>
                    </div>
                  ) : null}
                </div>

                {hasRowError ? (
                  <div className={clsx(styles.sendConditionRow)}>
                    <div className={clsx(styles.sendConditionFields)}>
                      <div className={clsx(styles.sendConditionMain)}>
                        {rowError.sendCondition ? (
                          <div className={clsx(styles.errorText)} data-field-error>
                            {rowError.sendCondition}
                          </div>
                        ) : null}
                      </div>
                      <div className={clsx(styles.sendConditionDays)}>
                        {rowError.day ? (
                          <div className={clsx(styles.errorText)} data-field-error>
                            {rowError.day}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {showRemoveColumn ? (
                      <span className={clsx(styles.sendConditionRemove)} aria-hidden />
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <AddRowButton
        label="Add Send Conditions"
        onClick={onAddSendCondition}
        disabled={!canAddNext}
      />
    </div>
  );
}
